import { useEffect, useMemo, useRef, useState } from "react"

import {
  useAdminAccounts,
  useUserAssignments,
  useAssignAccountToUser,
  useUnassignAccountFromUser,
  type AdminUser,
} from "@/api/hooks/admin"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog"

function showToast(msg: string, type: "success" | "error" = "success") {
  const el = document.createElement("div")
  el.textContent = msg
  el.className = `fixed top-4 right-4 z-[9999] rounded-lg px-4 py-3 text-sm font-medium shadow-lg ${
    type === "error"
      ? "bg-red-900/90 text-red-200 border border-red-700"
      : "bg-green-900/90 text-green-200 border border-green-700"
  }`
  document.body.appendChild(el)
  setTimeout(() => el.remove(), 5000)
}

interface Props {
  user: AdminUser
  onClose: () => void
}

export default function UserAccountsModal({ user, onClose }: Props) {
  const { data: accountsData } = useAdminAccounts()
  const { data: assignmentsData, refetch: refetchAssignments } =
    useUserAssignments(user.id)
  const assignAccount = useAssignAccountToUser()
  const unassignAccount = useUnassignAccountFromUser()

  const [dropdownOpen, setDropdownOpen] = useState(false)
  const [search, setSearch] = useState("")
  const dropdownRef = useRef<HTMLDivElement>(null)

  // All IDs as strings to avoid precision issues with large TikTok IDs
  const assignedIds = useMemo(() => {
    const set = new Set<string>()
    if (assignmentsData?.data?.accounts) {
      for (const acc of assignmentsData.data.accounts) {
        set.add(String(acc.id))
      }
    }
    return set
  }, [assignmentsData])

  const allAccounts = useMemo(() => accountsData?.data ?? [], [accountsData])

  const filteredOptions = useMemo(() => {
    const q = search.toLowerCase()
    return allAccounts.filter(
      (acc) =>
        (acc.advertiser_name?.toLowerCase().includes(q) ||
          acc.advertiser_id?.toLowerCase().includes(q)) &&
        !assignedIds.has(String(acc.id)),
    )
  }, [allAccounts, search, assignedIds])

  // Close dropdown on outside click
  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(e.target as Node)
      ) {
        setDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClick, true)
    return () => document.removeEventListener("mousedown", handleClick, true)
  }, [])

  function handleAssign(accountId: number) {
    assignAccount.mutate(
      { userId: user.id, accountId },
      {
        onSuccess: () => {
          refetchAssignments()
        },
        onError: (err) =>
          showToast(
            err instanceof Error ? err.message : "Error assigning account",
            "error",
          ),
      },
    )
  }

  function handleUnassign(accountId: number) {
    unassignAccount.mutate(
      { userId: user.id, accountId },
      {
        onSuccess: () => {
          refetchAssignments()
        },
        onError: (err) =>
          showToast(
            err instanceof Error ? err.message : "Error unassigning account",
            "error",
          ),
      },
    )
  }

  const assignedAccounts = assignmentsData?.data?.accounts ?? []

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-[900px] border-[#4a4a4a] bg-[#3a3a3a] text-white">
        <DialogHeader>
          <DialogTitle>Manage Accounts for {user.username}</DialogTitle>
          <DialogDescription className="text-white">
            Assign or unassign ad accounts for this user.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Assigned chips */}
          <div>
            <label className="mb-2 block text-sm font-semibold">
              Ad Accounts
            </label>
            <div className="flex min-h-[40px] max-h-[120px] flex-wrap gap-2 overflow-y-auto rounded-md border border-[#555] bg-[#2d2d2d] p-2">
              {assignedAccounts.length === 0 ? (
                <span className="py-2 text-sm text-white">
                  No accounts assigned
                </span>
              ) : (
                assignedAccounts.map((acc) => (
                  <span
                    key={acc.id}
                    className="inline-flex items-center gap-1 rounded-full bg-[#667eea]/20 px-2.5 py-1 text-xs font-medium text-[#8b9aff]"
                  >
                    {acc.advertiser_name}
                    <button
                      onClick={() => handleUnassign(acc.id)}
                      className="ml-0.5 text-[#8b9aff] hover:text-red-400"
                    >
                      &times;
                    </button>
                  </span>
                ))
              )}
            </div>
          </div>

          {/* Dropdown */}
          <div ref={dropdownRef} className="relative">
            <button
              type="button"
              onClick={() => setDropdownOpen(!dropdownOpen)}
              className="flex w-full items-center justify-between rounded-md border border-[#555] bg-[#2d2d2d] px-3 py-2 text-sm text-white"
            >
              <span>
                {assignedAccounts.length > 0
                  ? `${assignedAccounts.length} account(s) selected`
                  : "Select accounts..."}
              </span>
              <span className="text-xs">&#9660;</span>
            </button>

            {dropdownOpen && (
              <div className="absolute left-0 right-0 top-full z-50 mt-1 flex max-h-[50vh] flex-col rounded-lg border border-[#555] bg-[#2d2d2d] shadow-lg">
                <div className="border-b border-[#555] p-2">
                  <input
                    type="text"
                    placeholder="Search accounts..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="w-full rounded border border-[#555] bg-[#1a1a1a] px-3 py-1.5 text-sm text-white"
                  />
                </div>
                <div className="flex-1 overflow-y-auto p-1">
                  {filteredOptions.length === 0 ? (
                    <div className="py-3 text-center text-sm text-white">
                      No accounts available
                    </div>
                  ) : (
                    filteredOptions.map((acc) => (
                      <button
                        key={acc.id}
                        type="button"
                        className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm hover:bg-[#3a3a3a]"
                        onClick={() => handleAssign(acc.id)}
                      >
                        <span className="font-medium text-white">
                          {acc.advertiser_name}
                        </span>
                        <span className="text-xs text-white">
                          ({acc.advertiser_id})
                        </span>
                      </button>
                    ))
                  )}
                </div>
                <div className="flex justify-end border-t border-[#555] p-2">
                  <button
                    type="button"
                    onClick={() => setDropdownOpen(false)}
                    className="rounded-md border border-[#8fa0ef]/35 bg-[#7488e8] px-3 py-1 text-xs font-semibold text-white shadow-sm transition-colors hover:bg-[#687bda]"
                  >
                    Apply
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="rounded-md border border-[#7a808b]/45 bg-[#636b78] px-4 py-2 font-semibold text-white shadow-sm transition-colors hover:bg-[#58606c]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={() => {
                showToast("Account assignments have been saved")
                onClose()
              }}
              className="rounded-md border border-[#8fa0ef]/35 bg-[#7488e8] px-4 py-2 font-semibold text-white shadow-sm transition-colors hover:bg-[#687bda]"
            >
              Confirm
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
